import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const FileUploadZone = ({ 
  onFileUpload, 
  isUploading, 
  className = '',
  storageUsed = 0,
  storageLimit = 10,
  storagePercentage = 0,
  isStorageAtLimit = false
}) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);

  const handleDragOver = (e) => {
    e?.preventDefault();
    if (!isStorageAtLimit) {
      setIsDragOver(true);
    }
  };

  const handleDragLeave = (e) => {
    e?.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    setIsDragOver(false);
    
    if (isStorageAtLimit) {
      alert('Cannot upload files: Storage limit reached. Please free up space first.');
      return;
    }
    
    const files = Array.from(e?.dataTransfer?.files);
    
    // Check if files would exceed storage limit
    const filesSize = files?.reduce((sum, file) => sum + file?.size, 0);
    const filesSizeGB = filesSize / (1024 * 1024 * 1024);
    
    if (storageUsed + filesSizeGB > storageLimit) {
      const exceededBy = (storageUsed + filesSizeGB - storageLimit)?.toFixed(2);
      alert(`Cannot upload: This would exceed your storage limit by ${exceededBy} GB.`);
      return;
    }
    
    handleFiles(files);
  };

  const handleFileSelect = (e) => {
    if (isStorageAtLimit) {
      alert('Cannot upload files: Storage limit reached. Please free up space first.');
      return;
    }
    
    const files = Array.from(e?.target?.files);
    
    // Check if files would exceed storage limit
    const filesSize = files?.reduce((sum, file) => sum + file?.size, 0);
    const filesSizeGB = filesSize / (1024 * 1024 * 1024);
    
    if (storageUsed + filesSizeGB > storageLimit) {
      const exceededBy = (storageUsed + filesSizeGB - storageLimit)?.toFixed(2);
      alert(`Cannot upload: This would exceed your storage limit by ${exceededBy} GB.`);
      e.target.value = ''; // Clear the input
      return;
    }
    
    handleFiles(files);
  };

  const handleFiles = (files) => {
    if (files?.length > 0) {
      // Simulate upload progress
      setUploadProgress(0);
      const interval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 100) {
            clearInterval(interval);
            return 100;
          }
          return prev + 10;
        });
      }, 200);

      onFileUpload(files);
    }
  };

  const acceptedTypes = '.jpg,.jpeg,.png,.gif,.mp4,.mov,.avi,.dwg,.step,.stl,.pdf';
  const isNearLimit = storagePercentage > 80;

  return (
    <div
      className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-luxury ${
        isStorageAtLimit
          ? 'border-destructive/30 bg-destructive/5 cursor-not-allowed'
          : isDragOver
            ? 'border-accent bg-accent/5'
            : isNearLimit
              ? 'border-warning/50 bg-warning/5 hover:border-warning' :'border-border hover:border-accent/50'
      } ${className}`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={acceptedTypes}
        onChange={handleFileSelect}
        className="hidden"
        disabled={isStorageAtLimit}
      />
      {isUploading ? (
        <div className="space-y-4">
          <div className="w-16 h-16 mx-auto bg-accent/10 rounded-full flex items-center justify-center">
            <Icon name="Upload" size={32} color="var(--color-accent)" />
          </div>
          
          <div className="space-y-2">
            <p className="text-sm font-medium text-foreground">Uploading files...</p>
            <div className="w-full bg-muted rounded-full h-2">
              <div
                className="bg-accent h-2 rounded-full transition-all duration-300"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
            <p className="text-xs text-muted-foreground">{uploadProgress}% complete</p>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {isStorageAtLimit ? (
            <>
              <div className="w-16 h-16 mx-auto bg-destructive/10 rounded-full flex items-center justify-center">
                <Icon name="AlertTriangle" size={32} color="var(--color-destructive)" />
              </div>
              
              <div className="space-y-2">
                <h3 className="text-lg font-medium text-destructive">
                  Storage Limit Reached
                </h3>
                <p className="text-sm text-muted-foreground">
                  You've used {storageUsed} GB of your {storageLimit} GB storage limit
                </p>
                <p className="text-xs text-muted-foreground">
                  Delete some files to free up space or upgrade your plan
                </p>
              </div>
            </>
          ) : (
            <>
              <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center ${
                isNearLimit ? 'bg-warning/10' : 'bg-muted'
              }`}>
                <Icon 
                  name="Upload" 
                  size={32} 
                  color={isNearLimit ? 'var(--color-warning)' : 'var(--color-muted-foreground)'} 
                />
              </div>
              
              <div className="space-y-2">
                <h3 className={`text-lg font-medium ${isNearLimit ? 'text-warning' : 'text-foreground'}`}>
                  Drop files here or click to upload
                </h3>
                <p className="text-sm text-muted-foreground">
                  Support for images, videos, CAD files, and documents
                </p>
                <p className="text-xs text-muted-foreground">
                  Maximum file size: 500MB per file
                </p>
                {isNearLimit && (
                  <p className="text-xs text-warning font-medium">
                    ⚠️ Storage {storagePercentage?.toFixed(1)}% full - {(storageLimit - storageUsed)?.toFixed(2)} GB remaining
                  </p>
                )}
              </div>

              <Button
                variant={isNearLimit ? "destructive" : "outline"}
                onClick={() => fileInputRef?.current?.click()}
                className="mt-4"
                disabled={isStorageAtLimit}
              >
                <Icon name="FolderOpen" size={16} />
                Browse Files
              </Button>
            </>
          )}
        </div>
      )}
      {/* Storage Info and Supported File Types */}
      <div className="mt-6 pt-4 border-t border-border">
        {!isStorageAtLimit && (
          <>
            <div className="mb-4">
              <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                <span>Storage Used</span>
                <span>{storageUsed} GB / {storageLimit} GB</span>
              </div>
              <div className="w-full bg-muted rounded-full h-1">
                <div
                  className={`h-1 rounded-full transition-all duration-300 ${
                    isStorageAtLimit 
                      ? 'bg-destructive' 
                      : isNearLimit 
                        ? 'bg-warning' :'bg-success'
                  }`}
                  style={{ width: `${Math.min(storagePercentage, 100)}%` }}
                />
              </div>
            </div>
            <p className="text-xs text-muted-foreground mb-2">Supported formats:</p>
            <div className="flex flex-wrap justify-center gap-2">
              {['JPG', 'PNG', 'MP4', 'CAD', 'PDF']?.map((type) => (
                <span
                  key={type}
                  className="px-2 py-1 bg-muted text-xs font-medium text-muted-foreground rounded"
                >
                  {type}
                </span>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default FileUploadZone;